import { FormEvent, useState } from "react";
import * as projectService from "../services/projectService";
import type { CreateProjectPayload } from "../types/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import {
  ImageUploader,
  type ImageFieldValue,
} from "../components/forms/ImageUploader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";

const defaultForm: CreateProjectPayload = {
  name: "",
  symbol: "",
  category: "",
  description: "",
  status: "open",
  goal: 10_000,
  activeContributors: 0,
};

const defaultImageValue: ImageFieldValue = { mode: "none", previewUrl: null };

type UploadedGraphImage = {
  base64: string;
  previewUrl: string;
};

const AdminDashboardPage = () => {
  const [form, setForm] = useState<CreateProjectPayload>({ ...defaultForm });
  const [imageValue, setImageValue] =
    useState<ImageFieldValue>(defaultImageValue);
  const [graphImageUrls, setGraphImageUrls] = useState<string[]>([""]);
  const [uploadedGraphImages, setUploadedGraphImages] = useState<
    UploadedGraphImage[]
  >([]);
  const [options, setOptions] = useState<
    Array<{ amount: string; returnPercentage: string; durationDays: string }>
  >([{ amount: "1000", returnPercentage: "10", durationDays: "180" }]);
  const [expandedOptions, setExpandedOptions] = useState<Set<number>>(
    new Set([0])
  );
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const imageBase64 =
        imageValue.mode === "upload" || imageValue.mode === "base64"
          ? imageValue.base64
          : undefined;
      const imageUrl =
        imageValue.mode === "external" ? imageValue.externalUrl : undefined;

      const validOptions = options
        .map((opt) => ({
          amount: Number.parseFloat(opt.amount),
          returnPercentage: Number.parseFloat(opt.returnPercentage),
          durationDays: Number.parseInt(opt.durationDays, 10),
        }))
        .filter(
          (opt) =>
            Number.isFinite(opt.amount) &&
            opt.amount > 0 &&
            Number.isFinite(opt.returnPercentage) &&
            opt.returnPercentage > 0 &&
            Number.isInteger(opt.durationDays) &&
            opt.durationDays > 0
        );

      const newUrls = graphImageUrls
        .map((url) => url.trim())
        .filter((url) => url.length > 0);

      await projectService.createProject({
        ...form,
        imageBase64: imageBase64 || undefined,
        imageUrl: imageUrl || undefined,
        graphImageUrls: newUrls.length > 0 ? newUrls : undefined,
        graphImagesBase64:
          uploadedGraphImages.length > 0
            ? uploadedGraphImages.map((img) => img.base64)
            : undefined,
        investmentOptions: validOptions.length > 0 ? validOptions : undefined,
      });
      toast({
        title: t("admin.dashboardCreateSuccessTitle"),
        description: t("admin.dashboardCreateSuccessDescription", {
          name: form.name,
        }),
        variant: "success",
      });
      setForm({ ...defaultForm });
      setImageValue(defaultImageValue);
      setGraphImageUrls([""]);
      setUploadedGraphImages([]);
      setOptions([{ amount: "1000", returnPercentage: "10", durationDays: "180" }]);
      setExpandedOptions(new Set([0]));
    } catch (err) {
      toast({
        title: t("admin.dashboardCreateErrorTitle"),
        description:
          (err as string) ?? t("admin.dashboardCreateErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground dark:text-white">
          {t("admin.dashboardTitle")}
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">
          {t("admin.dashboardSubtitle")}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-3xl border border-border bg-muted/20 p-6 dark:border-white/10 dark:bg-white/5">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("admin.dashboardNameLabel")}</Label>
            <Input
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label>{t("admin.dashboardSymbolLabel")}</Label>
            <Input
              value={form.symbol}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  symbol: event.target.value.toUpperCase(),
                }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label>{t("admin.dashboardCategoryLabel")}</Label>
            <Input
              value={form.category}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, category: event.target.value }))
              }
              required
            />
          </div>
          <div className="space-y-2">
            <Label>{t("admin.dashboardStatusLabel")}</Label>
            <Select
              value={form.status}
              onValueChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  status: value as CreateProjectPayload["status"],
                }))
              }>
              <SelectTrigger className="border border-input bg-background shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-slate-800/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">
                  {t("admin.dashboardStatusOpen")}
                </SelectItem>
                <SelectItem value="closed">
                  {t("admin.dashboardStatusClosed")}
                </SelectItem>
                <SelectItem value="upcoming">
                  {t("admin.dashboardStatusUpcoming")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("admin.dashboardDescriptionLabel")}</Label>
          <Textarea
            value={form.description}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, description: event.target.value }))
            }
            required
            placeholder={t("admin.dashboardDescriptionPlaceholder")}
          />
        </div>

        <ImageUploader value={imageValue} onChange={setImageValue} />

        <div className="rounded-2xl border border-border bg-muted/40 p-6 shadow-lg dark:border-white/10 dark:bg-gradient-to-br dark:from-slate-900/60 dark:to-slate-800/40">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="text-base font-semibold text-foreground dark:text-white">
                Graphes / images supplémentaires
              </h3>
              <p className="text-xs text-muted-foreground dark:text-white/60">
                Ajoutez des URLs ou importez des fichiers (graphiques de prévision, visuels complémentaires)
                qui seront visibles sur la page de détail du projet.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-border text-foreground/90 hover:bg-muted dark:border-white/20 dark:text-white/80 dark:hover:bg-white/5"
                onClick={() => setGraphImageUrls((prev) => [...prev, ""])}>
                + URL
              </Button>
              <label className="inline-flex cursor-pointer items-center rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-100 dark:border-emerald-400/60 dark:bg-emerald-500/10 dark:text-emerald-200 dark:hover:bg-emerald-500/20">
                Importer
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const result = reader.result;
                      if (typeof result === "string") {
                        setUploadedGraphImages((prev) => [
                          ...prev,
                          {
                            base64: result,
                            previewUrl: result,
                          },
                        ]);
                      }
                    };
                    reader.readAsDataURL(file);
                    event.target.value = "";
                  }}
                />
              </label>
            </div>
          </div>

          <div className="space-y-3">
            {graphImageUrls.map((url, index) => (
              <div
                key={index}
                className="flex flex-col gap-2 rounded-xl border border-border bg-muted/40 p-3 md:flex-row md:items-center dark:border-white/10 dark:bg-black/30">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground dark:text-white/70">
                    URL de l'image #{index + 1}
                  </Label>
                  <Input
                    type="url"
                    placeholder="https://exemple.com/graphique.png"
                    value={url}
                    onChange={(event) =>
                      setGraphImageUrls((prev) =>
                        prev.map((item, i) =>
                          i === index ? event.target.value : item
                        )
                      )
                    }
                    className="mt-1 bg-background text-foreground border-border dark:bg-white/5 dark:text-white dark:border-white/10"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start text-xs text-red-300 hover:bg-red-500/10"
                  onClick={() =>
                    setGraphImageUrls((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                  disabled={graphImageUrls.length <= 1}>
                  Supprimer
                </Button>
              </div>
            ))}

            {uploadedGraphImages.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground dark:text-white/70">
                  Images importées
                </p>
                <div className="grid gap-3 md:grid-cols-3">
                  {uploadedGraphImages.map((img, index) => (
                    <div
                      key={index}
                      className="space-y-2 rounded-xl border border-emerald-400/40 bg-black/40 p-2">
                      <div className="aspect-video overflow-hidden rounded-lg bg-black/60">
                        <img
                          src={img.previewUrl}
                          alt={`Image importée ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs text-red-300 hover:bg-red-500/10"
                        onClick={() =>
                          setUploadedGraphImages((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }>
                        Supprimer
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("admin.dashboardGoalLabel")}</Label>
            <Input
              type="number"
              value={form.goal}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  goal: Number(event.target.value) || defaultForm.goal,
                }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Contributeurs actifs</Label>
            <Input
              type="number"
              value={form.activeContributors ?? 0}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  activeContributors: Number(event.target.value) || 0,
                }))
              }
            />
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/5 p-6 shadow-lg">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-emerald-300">
                Options d'investissement
              </h3>
              <p className="text-xs text-muted-foreground dark:text-white/60">
                Définissez plusieurs montants avec leur rendement et la durée du
                projet.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-emerald-400/60 text-emerald-200 hover:bg-emerald-500/10"
              onClick={() => {
                const newIndex = options.length;
                setOptions((prev) => [
                  ...prev,
                  { amount: "", returnPercentage: "", durationDays: "" },
                ]);
                // Réduire toutes les autres options et agrandir la nouvelle
                setExpandedOptions(new Set([newIndex]));
              }}>
              + Ajouter un montant
            </Button>
          </div>

          <div className="space-y-3">
            {options.map((opt, index) => {
              const amount = Number.parseFloat(opt.amount || "0");
              const percentage = Number.parseFloat(opt.returnPercentage || "0");
              const expectedGain =
                Number.isFinite(amount) && Number.isFinite(percentage)
                  ? (amount * percentage) / 100
                  : 0;
              const isExpanded = expandedOptions.has(index);
              const isFilled = opt.amount && opt.returnPercentage && opt.durationDays;

              return (
                <div
                  key={index}
                  className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
                  {/* En-tête cliquable pour toggle */}
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedOptions((prev) => {
                        const next = new Set(prev);
                        if (next.has(index)) {
                          next.delete(index);
                        } else {
                          next.add(index);
                        }
                        return next;
                      });
                    }}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
                        {index + 1}
                      </div>
                      {isFilled ? (
                        <div className="flex-1 text-left">
                          <p className="text-sm font-semibold text-foreground dark:text-white">
                            {Number.parseFloat(opt.amount || "0").toLocaleString()} $ • {opt.returnPercentage}% • {opt.durationDays} j
                          </p>
                          <p className="text-xs text-emerald-300">
                            Gain estimé: {expectedGain > 0 ? `${expectedGain.toLocaleString()} $` : "-"}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground dark:text-white/60">
                          Option #{index + 1} (non remplie)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isFilled && (
                        <span className="text-xs text-emerald-300 font-medium">
                          {isExpanded ? "Réduire" : "Agrandir"}
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground dark:text-white/60" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground dark:text-white/60" />
                      )}
                    </div>
                  </button>

                  {/* Contenu expandable */}
                  {isExpanded && (
                    <div className="px-4 pb-4 grid gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))] md:items-end border-t border-white/10 pt-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground dark:text-white/70">Montant ($)</Label>
                        <Input
                          type="number"
                          min={0}
                          value={opt.amount}
                          onChange={(event) =>
                            setOptions((prev) =>
                              prev.map((item, i) =>
                                i === index ? { ...item, amount: event.target.value } : item
                              )
                            )
                          }
                          className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white dark:border-white/10"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground dark:text-white/70">
                          Rendement (%)
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          step={0.1}
                          value={opt.returnPercentage}
                          onChange={(event) =>
                            setOptions((prev) =>
                              prev.map((item, i) =>
                                i === index
                                  ? { ...item, returnPercentage: event.target.value }
                                  : item
                              )
                            )
                          }
                          className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white dark:border-white/10"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground dark:text-white/70">
                          Durée (jours)
                        </Label>
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          value={opt.durationDays}
                          onChange={(event) =>
                            setOptions((prev) =>
                              prev.map((item, i) =>
                                i === index
                                  ? { ...item, durationDays: event.target.value }
                                  : item
                              )
                            )
                          }
                          className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white dark:border-white/10"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <div>
                          <p className="text-xs text-muted-foreground dark:text-white/60">Gain estimé</p>
                          <p className="text-sm font-semibold text-emerald-300">
                            {expectedGain > 0
                              ? `${expectedGain.toLocaleString()} $`
                              : "-"}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="self-start text-xs text-red-300 hover:bg-red-500/10"
                          onClick={() => {
                            setOptions((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                            setExpandedOptions((prev) => {
                              const next = new Set(prev);
                              next.delete(index);
                              // Réindexer les options restantes
                              const reindexed = new Set<number>();
                              next.forEach((idx) => {
                                if (idx > index) {
                                  reindexed.add(idx - 1);
                                } else {
                                  reindexed.add(idx);
                                }
                              });
                              return reindexed;
                            });
                          }}
                          disabled={options.length <= 1}>
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full md:w-auto"
          disabled={isSubmitting}>
          {isSubmitting
            ? t("admin.dashboardSubmitPublishing")
            : t("admin.dashboardSubmit")}
        </Button>
      </form>
    </div>
  );
};

export default AdminDashboardPage;
